import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Badge } from '~/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { Alert, AlertDescription } from '~/components/ui/alert';
import { ArrowLeft, CreditCard, DollarSign, Info, Loader2 } from 'lucide-react';
import { useCredits, usePurchaseCredits, useRefreshCredits } from '../../hooks/useCredits';
import { useAuth } from '../../contexts/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import { env } from '../../config/environment';
import { PaymentMethods } from './PaymentMethods';
import { apiClient } from '../../api/client';
import { useWebSocketPaymentMethods } from '../../hooks/useWebSocketPaymentMethods';

const stripePromise = loadStripe(env.STRIPE_PUBLISHABLE_KEY);

interface PaymentMethod {
  id: string;
  type: 'card';
  card: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
  created: number;
}

export function CreditManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: creditsData, isLoading, refetch: refetchCredits } = useCredits();

  // Force a fresh fetch when component mounts to ensure we have latest data
  React.useEffect(() => {
    refetchCredits();
  }, [refetchCredits]);

  // Debug logging
  React.useEffect(() => {
    console.log('CreditManagement - creditsData:', creditsData);
    console.log('CreditManagement - user.creditsBalance:', user?.creditsBalance);
    console.log('CreditManagement - final balance:', creditsData?.balance ?? user?.creditsBalance ?? 0);
  }, [creditsData, user?.creditsBalance]);



  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading credit information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard')}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Button>
        <h1 className="text-2xl font-bold">Credit Management</h1>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Current Balance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Current Balance
            </CardTitle>
            <CardDescription>
              Your available credits for document parsing
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-2">
                {creditsData?.balance ?? user?.creditsBalance ?? 0}
              </div>
              <p className="text-sm text-muted-foreground">
                Credits Available
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Pricing Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              How It Works
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm">1 Credit</span>
              <span className="text-sm font-medium">= 1 Page Parsed</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">Cost (USD)</span>
              <span className="text-sm font-medium">$0.10 per credit</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">Cost (AUD)</span>
              <span className="text-sm font-medium">$0.25 per credit</span>
            </div>
            <Alert className="mt-4">
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Credits are deducted only when documents are processed. 
                Creating projects and uploading files is free.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {/* Saved Payment Methods */}
        <PaymentMethods />

        {/* Purchase Credits */}
        <Card>
          <CardHeader>
            <CardTitle>Purchase Credits</CardTitle>
            <CardDescription>
              Buy credits to parse your bank statements
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CreditPurchaseForm />
          </CardContent>
        </Card>
      </div>

      {/* Transaction History */}
      {creditsData?.transactions && creditsData.transactions.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Recent Transactions</CardTitle>
            <CardDescription>
              Your recent credit transactions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {creditsData.transactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between p-3 border rounded"
                >
                  <div>
                    <p className="font-medium">{transaction.description}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(transaction.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge
                      variant={transaction.amount > 0 ? 'default' : 'secondary'}
                    >
                      {transaction.amount > 0 ? '+' : ''}{transaction.amount} credits
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      {transaction.type}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function CreditPurchaseForm() {
  const [credits, setCredits] = useState<number>(100);
  const [currency, setCurrency] = useState<'USD' | 'AUD'>('USD');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'new' | string>('new');
  const [savedPaymentMethods, setSavedPaymentMethods] = useState<PaymentMethod[]>([]);
  const [clientSecret, setClientSecret] = useState<string>('');
  const [isLoadingIntent, setIsLoadingIntent] = useState(false);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState<'configure' | 'confirm' | 'payment'>('configure');

  const purchaseCredits = usePurchaseCredits();
  const { data: creditsData } = useCredits();
  const queryClient = useQueryClient();
  const refreshCredits = useRefreshCredits();

  // Fetch saved payment methods on component mount
  React.useEffect(() => {
    fetchSavedPaymentMethods();
  }, []);

  const fetchSavedPaymentMethods = async () => {
    try {
      setIsLoadingPaymentMethods(true);
      const data = await apiClient.get<{ success: boolean; data?: { paymentMethods: PaymentMethod[] } }>(
        '/payments/payment-methods'
      );
      setSavedPaymentMethods(data?.data?.paymentMethods || []);
    } catch (err) {
      // Ignore errors - user can still use new payment method
      setSavedPaymentMethods([]);
    } finally {
      setIsLoadingPaymentMethods(false);
    }
  };

  // Handle real-time payment method change events via WebSocket
  const handlePaymentMethodsChanged = React.useCallback(() => {
    console.log('[CreditManagement] WebSocket payment methods change event received, fetching fresh data');
    fetchSavedPaymentMethods();
  }, []);

  // Use dedicated WebSocket hook for payment method change events
  useWebSocketPaymentMethods(handlePaymentMethodsChanged);



  // Refresh payment methods when entering confirm step
  React.useEffect(() => {
    if (step === 'confirm') {
      console.log('[CreditManagement] Entering confirm step, refreshing payment methods');
      fetchSavedPaymentMethods();
    }
  }, [step]);

  const pricing = creditsData?.pricing;
  const selectedPricing = pricing?.[currency];
  const totalCost = selectedPricing ? credits * selectedPricing.pricePerCredit : 0;

  const handleCreatePaymentIntent = async () => {
    if (!selectedPricing || credits <= 0) {
      return;
    }

    setIsLoadingIntent(true);
    setError(null);

    try {
      const response = await purchaseCredits.mutateAsync({
        credits,
        currency,
      });

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to create payment intent');
      }

      setClientSecret(response.data.clientSecret);
    } catch (err: any) {
      setError(err.message || 'Failed to initialize payment');
    } finally {
      setIsLoadingIntent(false);
    }
  };

  const handleReset = () => {
    setClientSecret('');
    setSuccess(false);
    setError(null);
    setCredits(100);
    setSelectedPaymentMethod('new');
    setStep('configure');
  };

  const handleContinueToConfirm = async () => {
    if (credits <= 0 || !selectedPricing) return;
    setError(null);
    setStep('confirm');

    // Refresh payment methods when entering confirm step
    console.log('[CreditManagement] Transitioning to confirm step, refreshing payment methods');
    await fetchSavedPaymentMethods();
  };

  const handleConfirmPayment = async () => {
    setStep('payment');
    
    if (selectedPaymentMethod !== 'new') {
      // Use saved payment method
      try {
        const data = await apiClient.post('/payments/quick-payment', {
          paymentMethodId: selectedPaymentMethod,
          credits,
          currency,
        });
        
        // Refresh credits data immediately
        await refreshCredits.mutateAsync();
        queryClient.invalidateQueries({ queryKey: ['credits'] });
        
        setSuccess(true);
      } catch (err: any) {
        setError(err.message);
        setStep('confirm');
        // Refresh payment methods in case the error was due to stale data
        console.log('[CreditManagement] Error occurred, refreshing payment methods on return to confirm');
        await fetchSavedPaymentMethods();
      }
    } else {
      // Create payment intent for new payment method
      await handleCreatePaymentIntent();
    }
  };

  if (success) {
    return (
      <div className="text-center space-y-4">
        <div className="text-green-600 text-lg font-medium">
          ✅ Payment Successful!
        </div>
        <p className="text-muted-foreground">
          Your credits have been added to your account.
        </p>
        <Button onClick={handleReset} variant="outline">
          Purchase More Credits
        </Button>
      </div>
    );
  }

  if (step === 'configure') {
    return (
      <div className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="credits">Number of Credits</Label>
            <Input
              id="credits"
              type="number"
              min="1"
              max="10000"
              value={credits}
              onChange={(e) => setCredits(parseInt(e.target.value) || 0)}
              placeholder="Enter number of credits"
            />
          </div>
          
          <div>
            <Label htmlFor="currency">Currency</Label>
            <Select value={currency} onValueChange={(value: 'USD' | 'AUD') => setCurrency(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD ($0.10 per credit)</SelectItem>
                <SelectItem value="AUD">AUD ($0.25 per credit)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {selectedPricing && (
          <div className="p-3 bg-muted rounded">
            <div className="flex justify-between text-sm">
              <span>{credits} credits × {selectedPricing.symbol}{selectedPricing.pricePerCredit}</span>
              <span className="font-medium">
                {selectedPricing.symbol}{totalCost.toFixed(2)} {currency}
              </span>
            </div>
          </div>
        )}

        <Button
          onClick={handleContinueToConfirm}
          className="w-full"
          disabled={credits <= 0 || !selectedPricing}
        >
          <CreditCard className="mr-2 h-4 w-4" />
          Continue to Payment Method
        </Button>
      </div>
    );
  }

  if (step === 'confirm') {
    return (
      <div className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Order Summary */}
        <div className="p-4 bg-muted rounded">
          <h4 className="font-medium mb-2">Order Summary</h4>
          <div className="flex justify-between text-sm mb-2">
            <span>{credits} credits × {selectedPricing?.symbol}{selectedPricing?.pricePerCredit}</span>
            <span className="font-medium">
              {selectedPricing?.symbol}{totalCost.toFixed(2)} {currency}
            </span>
          </div>
        </div>

        {/* Payment Method Selection */}
        <div className="space-y-3">
          <Label>Select Payment Method</Label>
          
          {isLoadingPaymentMethods ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="ml-2 text-sm">Loading payment methods...</span>
            </div>
          ) : (
            <>
              {savedPaymentMethods.length > 0 && (
                <div className="space-y-2">
                  {savedPaymentMethods.map((method) => (
                    <div key={method.id} className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id={method.id}
                        name="paymentMethod"
                        value={method.id}
                        checked={selectedPaymentMethod === method.id}
                        onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                        className="rounded border-gray-300"
                      />
                      <label htmlFor={method.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <span>💳</span>
                        <span className="capitalize">
                          {method.card.brand} •••• {method.card.last4}
                        </span>
                        <span className="text-muted-foreground">
                          {method.card.exp_month.toString().padStart(2, '0')}/{method.card.exp_year}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="new"
                  name="paymentMethod"
                  value="new"
                  checked={selectedPaymentMethod === 'new'}
                  onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                  className="rounded border-gray-300"
                />
                <label htmlFor="new" className="text-sm cursor-pointer">
                  Use new payment method
                </label>
              </div>
            </>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            onClick={() => setStep('configure')}
            variant="outline"
            className="flex-1"
          >
            Back
          </Button>
          <Button
            onClick={handleConfirmPayment}
            className="flex-1"
            disabled={isLoadingPaymentMethods}
          >
            Confirm Payment
          </Button>
        </div>
      </div>
    );
  }

  if (step === 'payment' && !clientSecret) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Processing payment...</p>
        </div>
      </div>
    );
  }

  return (
    <Elements 
      stripe={stripePromise} 
      options={{ 
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#0f172a',
            colorBackground: '#ffffff',
            colorText: '#0f172a',
            colorDanger: '#dc2626',
            fontFamily: 'system-ui, sans-serif',
            spacingUnit: '4px',
            borderRadius: '6px',
          },
        },
      }}
    >
      <PaymentForm 
        onSuccess={async () => {
          // Refresh credits data immediately after successful payment
          await refreshCredits.mutateAsync();
          queryClient.invalidateQueries({ queryKey: ['credits'] });
          setSuccess(true);
        }} 
        onError={setError}
        isProcessing={isProcessing}
        setIsProcessing={setIsProcessing}
        credits={credits}
        totalCost={totalCost}
        currency={currency}
        selectedPricing={selectedPricing}
      />
    </Elements>
  );
}

function PaymentForm({ 
  onSuccess, 
  onError, 
  isProcessing, 
  setIsProcessing,
  credits,
  totalCost,
  currency,
  selectedPricing 
}: {
  onSuccess: () => void | Promise<void>;
  onError: (error: string) => void;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
  credits: number;
  totalCost: number;
  currency: string;
  selectedPricing: any;
}) {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    onError('');

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: `${window.location.origin}/credits`,
        },
      });

      if (error) {
        throw new Error(error.message || 'Payment failed');
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        await onSuccess();
      }
      
    } catch (err: any) {
      onError(err.message || 'An error occurred during payment');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-3 bg-muted rounded">
        <div className="flex justify-between text-sm">
          <span>{credits} credits × {selectedPricing?.symbol}{selectedPricing?.pricePerCredit}</span>
          <span className="font-medium">
            {selectedPricing?.symbol}{totalCost.toFixed(2)} {currency}
          </span>
        </div>
      </div>

      <div>
        <Label>Payment Details</Label>
        <div 
          className="mt-2 p-3 border rounded" 
          role="group" 
          aria-label="Payment information"
          // Ensure no aria-hidden conflicts
          aria-hidden="false"
        >
          <PaymentElement 
            options={{
              layout: 'tabs',
              paymentMethodOrder: ['card'],
            }}
          />
        </div>
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={!stripe || !elements || isProcessing}
      >
        {isProcessing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing Payment...
          </>
        ) : (
          <>
            <DollarSign className="mr-2 h-4 w-4" />
            Pay {selectedPricing?.symbol}{totalCost.toFixed(2)} {currency}
          </>
        )}
      </Button>
    </form>
  );
}
